import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaSave,
  FaTimes,
  FaImage,
  FaSync,
  FaExclamationTriangle,
  FaCheckCircle,
} from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// ==================== HTML SANITIZATION ====================
const sanitizeHtml = (html: string): string => {
  if (!html) return "";
  let cleaned = html;
  cleaned = cleaned.replace(
    /<span style="background-color: transparent; color: rgb\(0, 0, 0\);">(.*?)<\/span>/g,
    "$1",
  );
  cleaned = cleaned.replace(/ style=""/g, "");
  cleaned = cleaned.replace(
    /<span style="color: rgb\(0, 0, 0\);">(.*?)<\/span>/g,
    "$1",
  );
  cleaned = cleaned.replace(
    /<span style="background-color: transparent;">(.*?)<\/span>/g,
    "$1",
  );
  cleaned = cleaned.replace(/<span>(.*?)<\/span>/g, "$1");
  cleaned = cleaned.replace(/\s+/g, " ");
  cleaned = cleaned.replace(/<p><br><\/p>/g, "<p><br></p>");
  return cleaned.trim();
};

// ==================== MODAL ====================
type ModalType = "success" | "error";

interface ModalState {
  isOpen: boolean;
  type: ModalType;
  title: string;
  message: string;
  onCloseAction?: () => void;
}

const MODAL_INITIAL: ModalState = {
  isOpen: false,
  type: "success",
  title: "",
  message: "",
};

const Modal = ({
  modal,
  onClose,
}: {
  modal: ModalState;
  onClose: () => void;
}) => {
  if (!modal.isOpen) return null;

  const isSuccess = modal.type === "success";

  const c = isSuccess
    ? {
        bar: "bg-green-500",
        iconBg: "bg-green-50",
        icon: "text-green-500",
        titleText: "text-green-700",
        btn: "bg-green-500 hover:bg-green-600",
      }
    : {
        bar: "bg-orange-500",
        iconBg: "bg-orange-50",
        icon: "text-orange-500",
        titleText: "text-orange-700",
        btn: "bg-orange-500 hover:bg-orange-600",
      };

  const handleClose = () => {
    modal.onCloseAction?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative z-10 w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className={`h-1 ${c.bar}`} />
        <div className="p-6">
          <div className="flex items-start gap-4 mb-5">
            <div className={`p-3 rounded-full flex-shrink-0 ${c.iconBg}`}>
              {isSuccess ? (
                <FaCheckCircle className={c.icon} size={20} />
              ) : (
                <FaExclamationTriangle className={c.icon} size={20} />
              )}
            </div>
            <div className="pt-0.5 flex-1">
              <h3 className={`text-base font-bold ${c.titleText}`}>
                {modal.title}
              </h3>
              <p className="text-gray-500 text-sm mt-0.5">{modal.message}</p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-300 hover:text-gray-500 transition-colors flex-shrink-0"
            >
              <FaTimes size={14} />
            </button>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleClose}
              className={`px-5 py-2 text-sm font-semibold text-white rounded-lg transition-colors ${c.btn}`}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== TYPES ====================
interface BlogFormData {
  title: string;
  tags: string;
  excerpt: string;
  content: string;
  publishDate: string;
  isHighlighted: boolean;
  language: string;
  category: string;
}

// ==================== MAIN COMPONENT ====================
const CreateEditBlog = () => {
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();

  const [formData, setFormData] = useState<BlogFormData>({
    title: "",
    tags: "",
    excerpt: "",
    content: "",
    publishDate: new Date().toISOString().split("T")[0],
    isHighlighted: false,
    language: "BENGALI",
    category: "AUSBILDUNG",
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [existingImageUrl, setExistingImageUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState("");
  const [modal, setModal] = useState<ModalState>(MODAL_INITIAL);

  // ---- Modal helpers ----
  const closeModal = () => setModal(MODAL_INITIAL);

  const showSuccess = (
    title: string,
    message: string,
    onCloseAction?: () => void,
  ) => {
    setModal({ isOpen: true, type: "success", title, message, onCloseAction });
  };

  const showError = (title: string, message: string) => {
    setModal({ isOpen: true, type: "error", title, message });
  };

  // ---- Quill config ----
  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ font: [] }],
        [{ size: [] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ["link", "image", "video"],
        ["clean"],
      ],
    }),
    [],
  );

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "video",
    "color",
    "background",
    "align",
  ];

  // ---- Fetch blog (edit mode) ----
  useEffect(() => {
    if (isEditMode && id) {
      fetchBlogData();
    }
  }, [id, isEditMode]);

  const fetchBlogData = async () => {
    setFetchLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/blogs/${id}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch blog");
      const data = await response.json();
      const cleanContent = sanitizeHtml(data.content || "");

      setFormData({
        title: data.title,
        tags: data.tags || "",
        excerpt: data.excerpt || "",
        content: "",
        publishDate: data.publishDate || new Date().toISOString().split("T")[0],
        isHighlighted: data.isHighlighted || false,
        language: data.language || "BENGALI",
        category: data.category || "AUSBILDUNG",
      });
      setExistingImageUrl(data.imageUrl || "");

      setTimeout(() => {
        setFormData((prev) => ({ ...prev, content: cleanContent }));
      }, 150);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load blog");
    } finally {
      setFetchLoading(false);
    }
  };

  // ---- Input handlers ----
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: (e.target as HTMLInputElement).checked,
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleContentChange = (value: string) => {
    setFormData({ ...formData, content: value });
  };

  // ---- Image (modals replace alerts) ----
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showError(
        "Invalid File",
        "Please select an image file (JPG, PNG, WEBP, or GIF).",
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showError(
        "File Too Large",
        "Image size must be less than 5MB. Please choose a smaller image.",
      );
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  // ---- Submit (modal replaces alert, navigate on OK) ----
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (!formData.title.trim()) throw new Error("Title is required");
      if (!formData.content.trim()) throw new Error("Content is required");
      if (!isEditMode && !imageFile) throw new Error("Blog image is required");

      const sanitizedFormData = {
        ...formData,
        content: sanitizeHtml(formData.content),
      };

      const formDataToSend = new FormData();
      formDataToSend.append("blogData", JSON.stringify(sanitizedFormData));
      if (imageFile) formDataToSend.append("image", imageFile);

      const url = isEditMode
        ? `${API_BASE}/api/admin/blogs/${id}`
        : `${API_BASE}/api/admin/blogs`;

      const response = await fetch(url, {
        method: isEditMode ? "PUT" : "POST",
        credentials: "include",
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to save blog");
      }

      showSuccess(
        isEditMode ? "Blog Updated" : "Blog Created",
        isEditMode
          ? `"${formData.title}" has been updated successfully.`
          : `"${formData.title}" has been created successfully.`,
        () => navigate("/admin/manage-blogs"),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save blog");
    } finally {
      setLoading(false);
    }
  };

  // ---- Loading screen ----
  if (fetchLoading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader
          icon={<FaImage />}
          title="Loading Blog..."
          breadcrumb={[
            { label: "Home", to: "/" },
            { label: "Dashboard", to: "/admin/dashboard" },
            { label: "Manage Blogs", to: "/admin/manage-blogs" },
            { label: isEditMode ? "Edit" : "Create" },
          ]}
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 text-center">
          <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
          <p className="text-gray-600">Loading blog data...</p>
        </main>
      </div>
    );
  }

  // ==================== RENDER ====================
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Modal */}
      <Modal modal={modal} onClose={closeModal} />

      {/* Header */}
      <PageHeader
        icon={<FaImage />}
        title={isEditMode ? "Edit Blog Post" : "Create New Blog Post"}
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Manage Blogs", to: "/admin/manage-blogs" },
          { label: isEditMode ? "Edit" : "Create" },
        ]}
      />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Blog Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                placeholder="Enter blog title"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                placeholder="Ausbildung, Germany, Career"
              />
              <p className="text-sm text-gray-500 mt-1">
                Example: Ausbildung, Germany, Career Advice
              </p>
            </div>

            {/* Excerpt */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Excerpt (Short Description)
              </label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                placeholder="Brief summary for blog cards"
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Blog Content <span className="text-red-500">*</span>
              </label>
              <div className="quill-wrapper">
                <ReactQuill
                  key={id || "new-blog"}
                  theme="snow"
                  value={formData.content}
                  onChange={handleContentChange}
                  modules={modules}
                  formats={formats}
                  placeholder="Write your blog content here..."
                />
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Tip: Paste plain text directly - unnecessary formatting will be
                cleaned automatically when you save
              </p>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Blog Featured Image{" "}
                {!isEditMode && <span className="text-red-500">*</span>}
              </label>

              {isEditMode && existingImageUrl && !imagePreview && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Current Image:</p>
                  <img
                    src={existingImageUrl}
                    alt="Current blog"
                    className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-300"
                  />
                </div>
              )}

              {imagePreview && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    New Image Preview:
                  </p>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-300"
                  />
                </div>
              )}

              <div className="flex items-center gap-4">
                <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2">
                  <FaImage /> Choose Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
                {imageFile && (
                  <span className="text-sm text-gray-600">
                    {imageFile.name}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Max file size: 5MB. Supported formats: JPG, PNG, WEBP, GIF
              </p>
            </div>

            {/* Publish Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Publish Date
              </label>
              <input
                type="date"
                name="publishDate"
                value={formData.publishDate}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
              />
            </div>

            {/* Hidden Language */}
            <input type="hidden" name="language" value={formData.language} />

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
              >
                <option value="AUSBILDUNG">Ausbildung</option>
                <option value="GERMAN_LANGUAGE">German Language</option>
                <option value="STUDY">Study</option>
                <option value="FSJ">FSJ</option>
                <option value="AU_PAIR">Au Pair</option>
                <option value="SETTLEMENT">Settlement</option>
                <option value="CAREER">Career</option>
                <option value="LIFE_IN_GERMANY">Life in Germany</option>
              </select>
              <p className="text-sm text-gray-500 mt-1">
                Select the main topic/category for this blog post
              </p>
            </div>

            {/* Highlighted */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                name="isHighlighted"
                checked={formData.isHighlighted}
                onChange={handleInputChange}
                className="w-5 h-5 text-[#009fe3] border-gray-300 rounded focus:ring-[#009fe3]"
              />
              <label className="text-sm font-semibold text-gray-700">
                Mark as Highlighted Post
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#009fe3] hover:bg-[#007cb5] text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <FaSync className="animate-spin" />
                    {isEditMode ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  <>
                    <FaSave />
                    {isEditMode ? "Update Blog" : "Create Blog"}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate("/admin/manage-blogs")}
                disabled={loading}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <FaTimes />
                Cancel
              </button>
            </div>
          </form>
        </div>
      </main>

      {/* Quill Styles */}
      <style>{`
        .quill-wrapper .ql-container {
          height: 500px;
          overflow-y: auto;
          border-bottom-left-radius: 0.5rem;
          border-bottom-right-radius: 0.5rem;
        }
        .quill-wrapper .ql-toolbar {
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
          background-color: #f9fafb;
        }
        .quill-wrapper .ql-editor {
          min-height: 450px;
          font-size: 16px;
          line-height: 1.6;
          overflow-y: auto;
        }
        .quill-wrapper .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
        .quill-wrapper .ql-container.ql-snow {
          border-color: #d1d5db;
        }
        .quill-wrapper:focus-within .ql-container.ql-snow {
          border-color: #009fe3;
          box-shadow: 0 0 0 2px rgba(0, 159, 227, 0.1);
        }
        .quill-wrapper .ql-editor img {
          max-width: 100%;
          height: auto;
        }
      `}</style>
    </div>
  );
};

export default CreateEditBlog;
